/** Value-entry stage renderer, driven by the active namespace's valueType.
 *
 * - string/integer with no fetchSuggestions: nothing extra (the inline text
 *   input in QuerySearchInput handles typing) — renders null.
 * - string/integer with fetchSuggestions: a keyboard/click-selectable
 *   suggestions list. Fully controlled — like NamespaceDropdown/OperatorMenu,
 *   the suggestion fetch itself (useSuggestions) lives in QuerySearchInput so
 *   its handleKeyDown can drive ArrowUp/Down/Enter against the same list.
 * - date: one DatePicker (two for a `range` operator).
 * - datetime: `range` gets the shared TimeWindowPanel. A relative pick stays
 *   relative (`now-15m..now`, resolved by the backend on every request, so a
 *   refreshed grid moves); fixed bounds are naive UTC `yyyy-MM-dd HH:mm:ss`
 *   (the backend make_aware's them as UTC). `exact` keeps
 *   its whole-day backend semantics (`field__date`) and renders the same
 *   date-only picker as `date`.
 *
 * The project convention (see BookingSyncPage / AuditLogPage) imports
 * `DatePicker` + `LocalizationProvider` from `@mui/x-date-pickers-pro` and the
 * v3 date-fns adapter from the `/AdapterDateFnsV3` subpath. A nested
 * LocalizationProvider is harmless, so we wrap locally to stay self-contained.
 */
import { Box, Button, CircularProgress, List, ListItem, ListItemButton, ListItemText, Paper, Popper } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { format, parse as parseDate } from 'date-fns';

import { boundToNaive, parseBound, TimeWindowPanel } from '@/components/TimeWindowPicker';

import type { NamespaceConfig, Suggestion } from './types';

const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';

interface Props {
  anchorEl: HTMLElement | null;
  /** Render the suggestion list in place (inside the chip editor's popover) instead
   *  of a popper under the search box. */
  inline?: boolean;
  config: NamespaceConfig;
  operator: string;
  /** The current in-progress text (for suggestion queries). */
  query: string;
  /** For a range's second value, the already-committed first value. */
  first?: string;
  suggestions: Suggestion[];
  loading: boolean;
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onSelectSuggestion: (value: string) => void;
  /** Commit the suggestion as a match-by-ID (operator `exact`, value = its id).
   *  Only wired for namespaces whose config declares the `exact` operator. */
  onSelectSuggestionById: (suggestion: Suggestion) => void;
  /** A date picked; `bound` says which picker ('from' only for a range's From). */
  onSelectDate: (value: string, bound: 'from' | 'to') => void;
  /** Commit both bounds of a datetime range at once (TimeWindowPanel Apply /
   *  preset click) — relative tokens or naive-UTC bounds. */
  onSelectRange: (from: string, to: string) => void;
}

/** Parse a formatted date string back into a Date (best-effort). */
function safeParse(value: string, fmt: string): Date | null {
  if (!value) return null;
  const d = parseDate(value, fmt, new Date());
  return Number.isNaN(d.getTime()) ? null : d;
}

export function ValueEditor({
  anchorEl,
  inline = false,
  config,
  operator,
  query,
  first,
  suggestions,
  loading,
  highlightedIndex,
  onHighlightChange,
  onSelectSuggestion,
  onSelectSuggestionById,
  onSelectDate,
  onSelectRange,
}: Props) {
  // Namespaces that declare `exact` (tag/tour/category/user) can pin the filter
  // to the picked row by its id — surfaced as a per-suggestion "by ID" action.
  const idMatch = config.operators.includes('exact');
  if (config.valueType === 'datetime' && operator === 'range') {
    // The panel edits instants; the wire keeps naive UTC wall-clock strings.
    // Keyed on the committed/in-progress values so an outside change (typed
    // text, stage transition) resets the panel's draft, and a Backspace
    // step-back from the second bound reopens with From prefilled.
    return (
      <TimeWindowPanel
        key={`${first ?? ''}|${query}`}
        initialFrom={parseBound(first ?? '')}
        initialTo={parseBound(query, { end: true })}
        allowFuture={config.futureWindows === true}
        onApply={([from, to]) => {
          if (from && to) onSelectRange(boundToNaive(from), boundToNaive(to));
        }}
      />
    );
  }
  // `datetime` + `exact` = a whole calendar day on the backend (`field__date`),
  // so it deliberately falls through to the date-only picker.
  if (config.valueType === 'date' || config.valueType === 'datetime') {
    const fmt = config.dateFormat ?? DEFAULT_DATE_FORMAT;
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.5 }}>
          {operator === 'range' && (
            <DatePicker
              label="From"
              value={safeParse(first ?? '', fmt)}
              onChange={(d) => d && onSelectDate(format(d, fmt), 'from')}
              slotProps={{ textField: { size: 'small' } }}
            />
          )}
          <DatePicker
            label={operator === 'range' ? 'To' : undefined}
            value={safeParse(query, fmt)}
            onChange={(d) => d && onSelectDate(format(d, fmt), 'to')}
            slotProps={{ textField: { size: 'small' } }}
          />
        </Box>
      </LocalizationProvider>
    );
  }

  if (!config.fetchSuggestions) return null;

  const list =
    loading && suggestions.length === 0 ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
        <CircularProgress size={16} />
      </Box>
    ) : (
      <List role="listbox" dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
        {suggestions.map((s: Suggestion, i) => (
          <ListItem
            key={s.value}
            disablePadding
            secondaryAction={
              idMatch && s.id != null ? (
                <Button
                  size="small"
                  aria-label="Match by ID"
                  title="Match this exact record by its ID (ignores same-named variants)"
                  sx={{ minWidth: 0, px: 0.75, fontSize: 11 }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectSuggestionById(s);
                  }}
                >
                  by&nbsp;ID&nbsp;#
                </Button>
              ) : undefined
            }
          >
            <ListItemButton
              role="option"
              aria-selected={i === highlightedIndex}
              selected={i === highlightedIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelectSuggestion(s.value);
              }}
              onMouseEnter={() => onHighlightChange(i)}
            >
              <ListItemText primary={s.label ?? s.value} secondary={s.description} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );

  if (inline) {
    // In the chip editor the list sits under its own input; empty means nothing to show.
    if (!loading && suggestions.length === 0) return null;
    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>{list}</Box>
    );
  }

  const open = Boolean(anchorEl) && (loading || suggestions.length > 0);
  return (
    <Popper open={open} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
      <Paper elevation={4} sx={{ mt: 0.5, minWidth: 260, maxWidth: 420 }}>
        {list}
      </Paper>
    </Popper>
  );
}
